import { bodyHealthId, bodyHealthTimestampCompare, validBodyHealthTimestamp } from '../../../supabase/functions/_shared/bodyHealthActionDomain';
import type { MetricRow } from './healthMap';

export interface HealthMetricImportResult { written: number; preserved_conflicts: number }
export interface HealthMetricImportQuery extends PromiseLike<unknown> {
  select(columns: string): HealthMetricImportQuery;
  abortSignal(signal: AbortSignal): HealthMetricImportQuery;
  setHeader(name: string, value: string): HealthMetricImportQuery;
}
// Erase the SDK's recursive database generics only at this structural boundary.
export interface HealthMetricImportClient { rpc: unknown }
interface HealthMetricImportRpcClient {
  rpc(name: 'import_native_health_metrics_v1', args: { p_records: MetricRow[] }): HealthMetricImportQuery;
}
export interface HealthMetricImportOptions {
  client: HealthMetricImportClient;
  rows: readonly MetricRow[];
  ownerId: string;
  bearer: string;
  signal: AbortSignal;
  assertCurrent: () => void;
  onConfirmed?: (delta: HealthMetricImportResult) => void;
}
const FIELDS = ['user_id', 'source', 'external_id', 'metric_type', 'value', 'unit', 'recorded_at', 'ended_at', 'metadata'];
const COLUMNS = 'id,user_id,source,external_id,metric_type,value,unit,recorded_at,ended_at,metadata';
const MAX_BATCH = 100;
const object = (value: unknown): value is Record<string, unknown> => value !== null && typeof value === 'object' && !Array.isArray(value);
const own = (value: object, key: string): boolean => Object.prototype.hasOwnProperty.call(value, key);
const fail = (reason: string): never => { throw new Error('health_metric_import_' + reason); };
const origin = (row: Pick<MetricRow, 'user_id' | 'source' | 'external_id'>): string => JSON.stringify([row.user_id, row.source, row.external_id]);
function jsonValue(value: unknown): boolean {
  if (value === null || typeof value === 'string' || typeof value === 'boolean') return true;
  if (typeof value === 'number') return Number.isFinite(value);
  if (Array.isArray(value)) return value.every(jsonValue);
  return object(value) && (Object.getPrototypeOf(value) === Object.prototype || Object.getPrototypeOf(value) === null)
    && Object.values(value).every(jsonValue);
}
function equalJson(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (Array.isArray(a) && Array.isArray(b)) return a.length === b.length && a.every((value, index) => equalJson(value, b[index]));
  if (!object(a) || !object(b)) return false;
  const keys = Object.keys(a);
  return keys.length === Object.keys(b).length && keys.every(key => own(b, key) && equalJson(a[key], b[key]));
}
function containsMetadata(actual: unknown, incoming: unknown): boolean {
  if (!object(incoming)) return equalJson(actual, incoming);
  return object(actual) && Object.keys(incoming).every(key => own(actual, key) && containsMetadata(actual[key], incoming[key]));
}
function sameTime(a: unknown, b: string): boolean {
  return validBodyHealthTimestamp(a) && bodyHealthTimestampCompare(a, b) === 0;
}
function inputs(input: readonly MetricRow[], owner: string): MetricRow[] {
  if (!Array.isArray(input) || input.length > MAX_BATCH) return fail('invalid');
  const seen = new Set<string>();
  return input.map(value => {
    if (!object(value) || Object.keys(value).length !== FIELDS.length || !FIELDS.every(key => own(value, key))
      || bodyHealthId(value.user_id) !== owner || typeof value.source !== 'string' || !['apple_health', 'health_connect'].includes(value.source)
      || typeof value.metric_type !== 'string' || !['steps', 'heart_rate', 'hrv'].includes(value.metric_type)
      || typeof value.value !== 'number' || !Number.isFinite(value.value)
      || typeof value.unit !== 'string' || !value.unit
      || typeof value.external_id !== 'string' || !value.external_id
      || !validBodyHealthTimestamp(value.recorded_at)
      || value.ended_at !== null && !validBodyHealthTimestamp(value.ended_at)
      || value.metadata !== null && (!object(value.metadata) || !jsonValue(value.metadata))) return fail('invalid');
    const row = { ...structuredClone(value), user_id: owner } as unknown as MetricRow;
    const key = origin(row);
    if (seen.has(key)) return fail('invalid');
    seen.add(key);
    return row;
  });
}
function confirm(data: unknown, rows: MetricRow[], owner: string, confirmedIds: Set<string>): void {
  if (!Array.isArray(data) || data.length !== rows.length) return fail('unconfirmed');
  const expected = new Map(rows.map(row => [origin(row), row]));
  const ids = new Set<string>();
  for (const raw of data) {
    if (!object(raw)) return fail('unconfirmed');
    let id: string, returnedOwner: string;
    try { id = bodyHealthId(raw.id); returnedOwner = bodyHealthId(raw.user_id); } catch { return fail('unconfirmed'); }
    const key = JSON.stringify([returnedOwner, raw.source, raw.external_id]), row = expected.get(key);
    if (!row || returnedOwner !== owner || ids.has(id) || confirmedIds.has(id) || raw.metric_type !== row.metric_type
      || raw.value !== row.value || raw.unit !== row.unit || !sameTime(raw.recorded_at, row.recorded_at)
      || (row.ended_at === null ? raw.ended_at !== null : !sameTime(raw.ended_at, row.ended_at))
      // Null input asks the RPC to preserve the existing JSON literally. An
      // object may gain old keys recursively; every supplied value must match.
      || !jsonValue(raw.metadata) || row.metadata !== null && !containsMetadata(raw.metadata, row.metadata)) return fail('unconfirmed');
    ids.add(id); expected.delete(key);
  }
  if (expected.size !== 0) return fail('unconfirmed');
  for (const id of ids) confirmedIds.add(id);
}
function protectedReply(reply: Record<string, unknown>): boolean {
  return reply.status === 400 && reply.data === null && object(reply.error) && reply.error.code === 'P0001'
    && reply.error.message === 'protected_body_health_record';
}

/**
 * The database trigger remains the protection authority. Only its exact returned
 * rejection proves that a bulk statement rolled back and permits bounded serial
 * isolation. Other errors or unconfirmed acknowledgements never retry. A
 * preserved conflict confirms a protective rejection, not the current content
 * or its cause. Callbacks retain confirmed progress if a later request fails.
 */
export async function importHealthMetricRecords(options: HealthMetricImportOptions): Promise<HealthMetricImportResult> {
  const { client, signal, assertCurrent, onConfirmed } = options;
  const owner = bodyHealthId(options.ownerId), bearer = options.bearer;
  const active = () => {
    assertCurrent();
    if (signal.aborted) throw new DOMException('Health metric import aborted', 'AbortError');
  };
  active();
  if (typeof bearer !== 'string' || !bearer || Array.from(bearer).some(character => /\s/.test(character) || character.charCodeAt(0) < 32 || character.charCodeAt(0) === 127)) return fail('invalid');
  const rows = inputs(options.rows, owner), total = { written: 0, preserved_conflicts: 0 }, confirmedIds = new Set<string>();
  const acknowledged = (delta: HealthMetricImportResult) => {
    active();
    total.written += delta.written; total.preserved_conflicts += delta.preserved_conflicts;
    onConfirmed?.(Object.freeze(delta));
  };
  const write = async (batch: MetricRow[]): Promise<boolean> => {
    active();
    const reply = await (client as HealthMetricImportRpcClient)
      .rpc('import_native_health_metrics_v1', { p_records: batch }).select(COLUMNS)
      .abortSignal(signal).setHeader('Authorization', 'Bearer ' + bearer);
    active();
    if (!object(reply)) return fail('unconfirmed');
    if (protectedReply(reply)) return false;
    if (reply.error !== null) throw reply.error ?? new Error('health_metric_import_unconfirmed');
    if (typeof reply.status !== 'number' || !Number.isInteger(reply.status) || reply.status < 200 || reply.status >= 300) return fail('unconfirmed');
    confirm(reply.data, batch, owner, confirmedIds);
    acknowledged({ written: batch.length, preserved_conflicts: 0 });
    return true;
  };
  if (rows.length !== 0 && !await write(rows)) {
    if (rows.length === 1) acknowledged({ written: 0, preserved_conflicts: 1 });
    else for (const row of rows) {
      if (!await write([row])) acknowledged({ written: 0, preserved_conflicts: 1 });
    }
  }
  active();
  return total;
}
